import {
  GetApiV1ThreeLetterCodePartnerAdminsClientsClientId200,
  GetApiV1ThreeLetterCodePartnerAdminsPartnerFeatureFlags200,
} from '@fukuri-admins/apis/client/gen/model';
import { getFeatureFlagLabel } from '~/utils/clientFeatureFlagUtils';
import { LegacyClientDetailPresentation } from '../../presentations';

type PresentationProps = Pick<
  React.ComponentProps<typeof LegacyClientDetailPresentation>,
  'clientDetail'
>;

/**
 * パートナー機能フラグの有効状態を考慮して、クライアント機能フラグの表示ステータスを返す。
 * パートナー側でフラグが無効の場合、クライアント側の設定に関わらず無効として扱う。
 *
 * @param featureKey - フィーチャーフラグのキー
 * @param clientFeatureStatus - クライアント側のフラグ有効状態
 * @param partnerFeatureFlags - パートナーの機能フラグ配列
 * @returns パートナー・クライアント双方で有効な場合のみ true
 */
const getFeatureFlagStatus = (
  featureKey: string,
  clientFeatureStatus: boolean,
  partnerFeatureFlags: GetApiV1ThreeLetterCodePartnerAdminsPartnerFeatureFlags200['partner_feature_flags']
): boolean => {
  const partnerFlag = partnerFeatureFlags.find(f => f.feature_key === featureKey);
  return partnerFlag?.enabled ? clientFeatureStatus : false;
};

/**
 * APIレスポンス形式のフィーチャーフラグ配列をPresentation層の形式に変換する。
 *
 * @param clientFeatureFlags - APIから取得したクライアントフィーチャーフラグの配列。
 * @param partnerFeatureFlags - パートナーのフィーチャーフラグの配列。
 * @returns Presentation層向けにフォーマットされたフィーチャーフラグの配列。
 *          各要素は `featureKey`・`isEnabled`・`label` プロパティを持つ。
 */
export const convertClientFeatureFlags = (
  clientFeatureFlags: GetApiV1ThreeLetterCodePartnerAdminsClientsClientId200['client']['client_feature_flags'],
  partnerFeatureFlags: GetApiV1ThreeLetterCodePartnerAdminsPartnerFeatureFlags200['partner_feature_flags']
): PresentationProps['clientDetail']['clientFeatureFlags'] => {
  return clientFeatureFlags.map(flag => ({
    featureKey: flag.feature_key,
    isEnabled: getFeatureFlagStatus(flag.feature_key, flag.enabled, partnerFeatureFlags),
    label: getFeatureFlagLabel(flag.feature_key),
  }));
};
